import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { expectedDeliveryDate, formatCurrency, formatDeliveryDate, formatNumber, getLocalizedField, isSupplierBackorder } from "@/lib/product";

const Besteld = () => {
  const { nummer } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const location = useLocation();
  const result = location.state?.result;
  const lines = location.state?.lines || [];
  const opmerking = location.state?.opmerking;
  const referentie = location.state?.referentie;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8" data-testid="confirmation-page">
      <Card className="p-6 sm:p-8">
        <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]">
          <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-display text-2xl font-semibold tracking-tight">
          {t("confirmation.title")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("confirmation.subtitle")}</p>

        <div className="mt-6 rounded-md border bg-secondary p-4">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            {t("confirmation.ordernummer")}
          </p>
          <p
            className="mt-1 font-display text-xl font-semibold tabular-nums"
            data-testid="confirmation-ordernummer"
          >
            {result?.ordernummer || nummer}
          </p>
          {result?.totaal != null && (
            <p className="mt-3 text-sm">
              <span className="text-muted-foreground">{t("confirmation.total")}: </span>
              <span className="font-semibold tabular-nums" data-testid="confirmation-total">
                {formatCurrency(result.totaal, lang)}
              </span>
            </p>
          )}
          {result?.factuurnummer && (
            <p className="mt-2 text-sm">
              <span className="text-muted-foreground">{t("confirmation.invoiceNumber")}: </span>
              <span className="font-semibold tabular-nums" data-testid="confirmation-invoice-number">
                {result.factuurnummer}
              </span>
            </p>
          )}
        </div>

        {lines.length > 0 && (
          <>
            <Separator className="my-6" />
            <h2 className="font-display text-base font-semibold">{t("confirmation.lines")}</h2>
            <div className="mt-3 divide-y">
              {lines.map((line) => (
                <div
                  key={line.product_id}
                  className="flex items-start justify-between gap-4 py-3"
                  data-testid="confirmation-line"
                >
                  <div>
                    <p className="text-sm font-medium">
                      {getLocalizedField(line.product, "naam", lang)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatNumber(line.aantal, lang)} ×{" "}
                      {formatCurrency(line.product?.verkoopprijs, lang)} {t("price.exclVat")}
                    </p>
                    {isSupplierBackorder(line.product) && (
                      <p className="mt-1 text-xs font-medium text-[hsl(var(--warning))]" data-testid="confirmation-line-supplier">
                        {t("stock.supplierShort", { datum: formatDeliveryDate(expectedDeliveryDate(), lang) })}
                      </p>
                    )}
                  </div>
                  <p className="shrink-0 text-sm font-semibold tabular-nums">
                    {formatCurrency(
                      Number(line.product?.verkoopprijs || 0) * Number(line.aantal || 0),
                      lang,
                    )}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}

        {referentie && (
          <div className="mt-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">{t("confirmation.referentie")}</p>
            <p className="mt-1 text-sm font-medium" data-testid="confirmation-referentie">{referentie}</p>
          </div>
        )}
        {opmerking && (
          <div className="mt-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              {t("confirmation.opmerking")}
            </p>
            <p className="mt-1 text-sm" data-testid="confirmation-opmerking">
              {opmerking}
            </p>
          </div>
        )}

        <p className="mt-6 text-sm text-muted-foreground">{t("confirmation.nextSteps")}</p>

        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild data-testid="confirmation-orders-button">
            <Link to="/mijn-bestellingen">{t("confirmation.toOrders")}</Link>
          </Button>
          <Button variant="outline" asChild data-testid="confirmation-catalog-button">
            <Link to="/producten">{t("confirmation.toCatalog")}</Link>
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default Besteld;
